import { Link } from "react-router-dom";
import { Zap, FileText, IndianRupee, ShieldCheck } from "lucide-react";

/**
 * Single-fold landing page. Strict copy compliance — no extra sections.
 * Brand: crimson #C5283F, coral #F05C3F, amber #FFAD27, canvas #FFF8F1,
 * ink #221A16. Font: Plus Jakarta Sans (loaded in index.html).
 */
export default function LandingPage() {
  return (
    <div className="h-svh min-h-[600px] bg-[var(--canvas)] text-[var(--ink)] flex flex-col overflow-hidden">
      <FacetsBackdrop />

      {/* Header */}
      <header className="relative z-10 px-4 sm:px-8 lg:px-12 py-5 lg:py-6 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="brand-header">
          <img src="/logo.svg" alt="Infin Entities"
               className="h-7 sm:h-8 w-auto" draggable="false" />
          <span className="font-display text-[15px] sm:text-base font-bold tracking-[0.22em] text-[var(--ink)] hidden sm:inline">
            INFIN&nbsp;ENTITIES
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/login" data-testid="nav-login"
                className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2 transition-colors">
            Sign in
          </Link>
        </div>
      </header>

      {/* Fold */}
      <main className="relative z-10 flex-1 px-4 sm:px-8 lg:px-12 pb-4 sm:pb-6 lg:pb-8">
        <div className="h-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6 xl:col-span-7 flex flex-col justify-center">
            <div className="text-[11px] sm:text-xs font-semibold tracking-[0.28em] text-[var(--crimson)] mb-4 sm:mb-5"
                 data-testid="hero-eyebrow">
              INFIN ENTITIES
            </div>

            <h1 className="font-display font-extrabold leading-[1.04] tracking-tight text-[clamp(2rem,5.4vw,4rem)]"
                data-testid="hero-h1">
              <span className="bg-gradient-to-r from-[var(--crimson)] to-[var(--amber)] bg-clip-text text-transparent">
                Lightning-fast
              </span>{" "}
              company registration.
              <br className="hidden sm:block" />
              <span className="text-[var(--ink)]">One chat. One price.</span>
            </h1>

            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-[var(--ink-2)] max-w-xl leading-relaxed"
               data-testid="hero-sub">
              Chat with Infi, upload PAN &amp; Aadhaar — licensed CAs and Company
              Secretaries handle the rest.
            </p>

            {/* Four value lines */}
            <ul className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 max-w-xl"
                data-testid="hero-bullets">
              <Bullet icon={<Zap size={15} fill="currentColor" strokeWidth={0} />}>
                Talk or type — English, Hindi, Hinglish
              </Bullet>
              <Bullet icon={<FileText size={15} />}>
                Upload documents. Infi fills the forms.
              </Bullet>
              <Bullet icon={<IndianRupee size={15} />}>
                ₹19,999 all-inclusive. Nothing hidden.
              </Bullet>
              <Bullet icon={<ShieldCheck size={15} />}>
                Encrypted. Aadhaar always masked.
              </Bullet>
            </ul>

            {/* CTA */}
            <div className="mt-6 sm:mt-7 flex flex-col items-start gap-2">
              <Link
                to="/login"
                data-testid="hero-cta"
                className="btn-crimson inline-flex items-center justify-center rounded-xl px-7 py-3.5 text-base font-semibold shadow-[0_10px_30px_-12px_rgba(197,40,63,0.45)]"
              >
                Start free with Google
              </Link>
              <p className="text-xs sm:text-[13px] text-[var(--ink-mute)]"
                 data-testid="cta-microcopy">
                Pay only when your application is complete.
              </p>
            </div>
          </div>

          {/* RIGHT — chat mockup */}
          <div className="lg:col-span-6 xl:col-span-5 hidden lg:flex items-center justify-center"
               data-testid="hero-mockup">
            <ChatMockup />
          </div>

          {/* Mobile: mockup peeks below CTA */}
          <div className="lg:hidden -mt-1">
            <ChatMockup compact />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-4 sm:px-8 lg:px-12 py-3 sm:py-3.5 border-t border-[var(--border-c)] bg-[var(--canvas)]/80 backdrop-blur-sm"
              data-testid="footer">
        <div className="max-w-7xl mx-auto text-[11px] sm:text-xs text-[var(--ink-mute)] flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>Filed by licensed CA/CS professionals</span>
          <Dot />
          <span>A Mavenstry Private Limited product</span>
          <Dot />
          <Link to="/terms" className="hover:text-[var(--ink-2)] transition-colors">Terms</Link>
          <Dot />
          <Link to="/privacy" className="hover:text-[var(--ink-2)] transition-colors">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}

function Dot() {
  return <span className="opacity-40">·</span>;
}

function Bullet({ icon, children }) {
  return (
    <li className="flex items-start gap-2.5 text-[14px] sm:text-[15px] text-[var(--ink)]">
      <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-[var(--amber)]/15 text-[var(--amber)] shrink-0">
        {icon}
      </span>
      <span className="leading-snug">{children}</span>
    </li>
  );
}

/* Faceted-hexagon backdrop — large, low-opacity drift behind the right side */
function FacetsBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="infin-facets absolute -right-[18%] top-[8%] w-[78%] max-w-[1100px] opacity-[0.06] sm:opacity-[0.07]">
        <svg viewBox="0 0 237.51 127.58" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <g>
            <path fill="#C5283F"
                  d="M118.75 98.07V29.51L74.8 4.13c-9.55-5.51-21.31-5.51-30.85 0L15.43 20.6C5.88 26.11 0 36.3 0 47.32v32.94c0 11.02 5.88 21.21 15.43 26.72l28.53 16.47c9.55 5.51 21.31 5.51 30.85 0l43.95-25.38Z" />
            <path fill="#F05C3F"
                  d="M237.51 80.26V47.32c0-11.02-5.88-21.21-15.43-26.72l-28.53-16.47c-9.55-5.51-21.31-5.51-30.85 0l-43.95 25.38v68.56l43.95 25.38c9.55 5.51 21.31 5.51 30.85 0l28.53-16.47c9.55-5.51 15.43-15.7 15.43-26.72Z" />
            <path fill="#FFAD27"
                  d="M162.71 4.13l-43.95 25.38-59.38 34.28v63.79c5.33 0 10.65-1.38 15.43-4.13l43.95-25.38 59.38-34.28V0c-5.33 0-10.65 1.38-15.43 4.13Z" />
          </g>
        </svg>
      </div>
      {/* Soft warm radial wash from top-right */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_60%_at_85%_15%,rgba(255,173,39,0.10),transparent_55%)]" />
    </div>
  );
}

/* Static chat mockup card — Infi's bubbles in soft crimson tint, founder in
   neutral, the name-check chip in amber. A single typing-dot trio at the
   bottom hints at liveness without animating message content. */
function ChatMockup({ compact = false }) {
  return (
    <div className={[
      "w-full max-w-md relative",
      compact ? "max-w-full" : "",
    ].join(" ")} data-testid="hero-chat-card">
      {/* Thin amber top border */}
      <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[var(--amber)] to-transparent" />
      <div className="bg-white rounded-2xl shadow-[0_30px_60px_-30px_rgba(34,26,22,0.30),0_2px_8px_-2px_rgba(34,26,22,0.08)] border border-[var(--border-c)] overflow-hidden">
        {/* Card header */}
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-[var(--border-c)] bg-gradient-to-b from-white to-[var(--canvas)]">
          <span className="w-2 h-2 rounded-full bg-[var(--crimson)]" />
          <span className="text-xs font-semibold tracking-wide text-[var(--ink)]">Infi</span>
          <span className="text-[10px] text-[var(--ink-mute)]">· assistant</span>
          <span className="ml-auto text-[10px] text-[var(--ink-mute)] font-mono">INFIN-2026</span>
        </div>

        <div className="p-4 space-y-3">
          <Bubble who="infi">
            Hi! I&apos;m Infi <span aria-hidden>👋</span> What will your company do?
          </Bubble>
          <Bubble who="founder">
            We&apos;re building a SaaS for boutique hotels
          </Bubble>
          <Bubble who="infi">
            Love it. Upload the directors&apos; PAN &amp; Aadhaar — I&apos;ll fill in
            everything else.
            <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold bg-[var(--amber)]/15 text-[#7A4A00] px-2 py-1 rounded-full border border-[var(--amber)]/30">
              <span aria-hidden>✓</span> Name check passed
            </div>
          </Bubble>

          <div className="pt-1 pl-1 flex items-center gap-1" aria-label="Infi is typing">
            <span className="infin-typing-dot" />
            <span className="infin-typing-dot" />
            <span className="infin-typing-dot" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ who, children }) {
  const isInfi = who === "infi";
  return (
    <div className={["flex", isInfi ? "" : "justify-end"].join(" ")}>
      <div
        className={[
          "max-w-[88%] text-[13.5px] leading-relaxed rounded-2xl px-3.5 py-2.5",
          isInfi
            ? "bg-[rgba(197,40,63,0.06)] text-[var(--ink)] border border-[rgba(197,40,63,0.12)] rounded-bl-md"
            : "bg-[var(--canvas)] text-[var(--ink)] border border-[var(--border-c)] rounded-br-md",
        ].join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
